import type { FC } from 'react'
import { useState, useCallback, useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Typography, useTheme } from '@mui/material'
import type { ClaudeSession } from '@/features/events/conversation/state/types'
import {
  getDisplayHostName,
  getDisplayProjectName,
  getSessionLatestTimestamp,
} from '@/features/events/conversation/state/types'
import type {
  SessionTreeProps,
  TreeNodeStyleConfig,
  ContentContainerConfig,
  TreeSortOrder,
} from './types/SessionTreeProps'
import { getTreeDefaults } from './defaults'
import type { HostGroup, ProjectGroup } from './renderers'
import { HostNodeRenderer } from './renderers'

type ExpansionState = Record<string, boolean>

export const SessionTree: FC<SessionTreeProps> = ({
  sessions,
  renderSessionOrchestrator,
  config,
  nodeConfig,
  contentContainerConfig,
  actionSlots,
  statSlots,
  emptyMessage = 'No sessions available',
  externalExpansion,
}) => {
  const theme = useTheme()
  const treeDefaults = useMemo(() => getTreeDefaults(theme.palette.mode), [theme.palette.mode])

  const sortOrder = config.sortOrder ?? 'newest'
  const hostGroups = useMemo(() => buildHostGroups(sessions, sortOrder), [sessions, sortOrder])

  const mergedHostConfig: TreeNodeStyleConfig = useMemo(
    () => ({
      ...treeDefaults.nodeConfig.host,
      ...nodeConfig?.host,
    }),
    [treeDefaults, nodeConfig],
  )

  const mergedProjectConfig: TreeNodeStyleConfig = useMemo(
    () => ({
      ...treeDefaults.nodeConfig.project,
      ...nodeConfig?.project,
    }),
    [treeDefaults, nodeConfig],
  )

  const mergedSessionConfig: TreeNodeStyleConfig = useMemo(
    () => ({
      ...treeDefaults.nodeConfig.session,
      ...nodeConfig?.session,
    }),
    [treeDefaults, nodeConfig],
  )

  const mergedContentConfig: ContentContainerConfig = useMemo(
    () => ({
      ...treeDefaults.contentContainerConfig,
      ...contentContainerConfig,
    }),
    [treeDefaults, contentContainerConfig],
  )

  const [expansionState, setExpansionState] = useState<ExpansionState>(() =>
    loadInitialExpansionState(config.storageKeyPrefix),
  )

  const effectiveIsExpanded = useCallback(
    (key: string): boolean => {
      if (externalExpansion) return externalExpansion.isExpanded(key)
      return expansionState[key] ?? config.defaultExpanded ?? true
    },
    [externalExpansion, expansionState, config.defaultExpanded],
  )

  const effectiveToggle = useCallback(
    (key: string) => {
      if (externalExpansion) {
        externalExpansion.onToggle(key)
        return
      }
      setExpansionState((prev) => {
        const newState = { ...prev, [key]: !prev[key] }
        if (config.persistExpansion && config.storageKeyPrefix) {
          saveExpansionState(config.storageKeyPrefix, newState)
        }
        return newState
      })
    },
    [externalExpansion, config.persistExpansion, config.storageKeyPrefix],
  )

  const showCounts = config.showCounts !== false

  const getHostActions = useCallback(
    (hostname: string, hostSessions: ClaudeSession[]) => {
      return actionSlots?.host?.(hostname, hostSessions)
    },
    [actionSlots],
  )

  const getHostStats = useCallback(
    (hostSessions: ClaudeSession[]) => {
      return statSlots?.host?.(hostSessions)
    },
    [statSlots],
  )

  const getProjectActions = useCallback(
    (projectKey: string, projectSessions: ClaudeSession[]) => {
      return actionSlots?.project?.(projectKey, projectSessions)
    },
    [actionSlots],
  )

  const getProjectStats = useCallback(
    (projectSessions: ClaudeSession[]) => {
      return statSlots?.project?.(projectSessions)
    },
    [statSlots],
  )

  const getSessionActions = useCallback(
    (session: ClaudeSession) => {
      return actionSlots?.session?.(session, [session])
    },
    [actionSlots],
  )

  const getSessionPersistentActions = useCallback(
    (session: ClaudeSession) => {
      return actionSlots?.persistentSession?.(session, [session])
    },
    [actionSlots],
  )

  if (hostGroups.length === 0) {
    return (
      <Typography color="text.secondary" sx={emptyMessageSx}>
        {emptyMessage}
      </Typography>
    )
  }

  return (
    <Box sx={treeContainerSx}>
      {hostGroups.map((host) => (
        <HostNodeRenderer
          key={host.hostname}
          host={host}
          hostIcon={config.icons.host}
          hostStyleConfig={mergedHostConfig}
          projectIcon={config.icons.project}
          projectStyleConfig={mergedProjectConfig}
          sessionIconConfig={config.icons.session}
          sessionStyleConfig={mergedSessionConfig}
          contentContainerConfig={mergedContentConfig}
          expandPosition={config.expandTogglePosition}
          showCounts={showCounts}
          isExpanded={effectiveIsExpanded}
          onToggle={effectiveToggle}
          hostActions={getHostActions(host.hostname, host.sessions)}
          hostStats={getHostStats(host.sessions)}
          getProjectActions={getProjectActions}
          getProjectStats={getProjectStats}
          getSessionActions={getSessionActions}
          getSessionPersistentActions={getSessionPersistentActions}
          renderOrchestrator={renderSessionOrchestrator}
        />
      ))}
    </Box>
  )
}

function buildHostGroups(
  sessions: readonly ClaudeSession[],
  sortOrder: TreeSortOrder,
): HostGroup[] {
  const hostMap = new Map<string, Map<string, ClaudeSession[]>>()

  for (const session of sessions) {
    const hostname = getDisplayHostName(session)
    const projectKey = session.contextWorkDirectory

    let projectMap = hostMap.get(hostname)
    if (!projectMap) {
      projectMap = new Map()
      hostMap.set(hostname, projectMap)
    }

    let projectSessions = projectMap.get(projectKey)
    if (!projectSessions) {
      projectSessions = []
      projectMap.set(projectKey, projectSessions)
    }

    projectSessions.push(session)
  }

  const result: HostGroup[] = []

  for (const [hostname, projectMap] of hostMap) {
    const projects: ProjectGroup[] = []
    const allHostSessions: ClaudeSession[] = []

    for (const [projectKey, projectSessions] of projectMap) {
      const projectName = projectSessions[0]
        ? getDisplayProjectName(projectSessions[0])
        : projectKey

      projects.push({
        projectKey,
        projectName,
        sessions: projectSessions,
      })

      allHostSessions.push(...projectSessions)
    }

    result.push({
      hostname,
      projects,
      sessions: allHostSessions,
    })
  }

  return applySortOrder(result, sortOrder)
}

function applySortOrder(groups: HostGroup[], sortOrder: TreeSortOrder): HostGroup[] {
  const sessionComparator = createSessionComparator(sortOrder)
  const projectComparator = createProjectComparator(sortOrder)
  const hostComparator = createHostComparator(sortOrder)

  for (const host of groups) {
    for (const project of host.projects) {
      project.sessions.sort(sessionComparator)
    }
    host.projects.sort(projectComparator)
  }

  groups.sort(hostComparator)

  return groups
}

function createSessionComparator(
  sortOrder: TreeSortOrder,
): (a: ClaudeSession, b: ClaudeSession) => number {
  switch (sortOrder) {
    case 'newest':
      return (a, b) => getSessionLatestTimestamp(b) - getSessionLatestTimestamp(a)
    case 'oldest':
      return (a, b) => getSessionLatestTimestamp(a) - getSessionLatestTimestamp(b)
    case 'alpha-asc':
      return (a, b) => (a.sessionName ?? '').localeCompare(b.sessionName ?? '')
    case 'alpha-desc':
      return (a, b) => (b.sessionName ?? '').localeCompare(a.sessionName ?? '')
  }
}

function createProjectComparator(
  sortOrder: TreeSortOrder,
): (a: ProjectGroup, b: ProjectGroup) => number {
  switch (sortOrder) {
    case 'newest':
      return (a, b) => getProjectLatestTimestamp(b) - getProjectLatestTimestamp(a)
    case 'oldest':
      return (a, b) => getProjectLatestTimestamp(a) - getProjectLatestTimestamp(b)
    case 'alpha-asc':
      return (a, b) => a.projectName.localeCompare(b.projectName)
    case 'alpha-desc':
      return (a, b) => b.projectName.localeCompare(a.projectName)
  }
}

function createHostComparator(sortOrder: TreeSortOrder): (a: HostGroup, b: HostGroup) => number {
  switch (sortOrder) {
    case 'newest':
      return (a, b) => getHostLatestTimestamp(b) - getHostLatestTimestamp(a)
    case 'oldest':
      return (a, b) => getHostLatestTimestamp(a) - getHostLatestTimestamp(b)
    case 'alpha-asc':
      return (a, b) => a.hostname.localeCompare(b.hostname)
    case 'alpha-desc':
      return (a, b) => b.hostname.localeCompare(a.hostname)
  }
}

function getProjectLatestTimestamp(project: ProjectGroup): number {
  let latest = 0
  for (const session of project.sessions) {
    const timestamp = getSessionLatestTimestamp(session)
    if (timestamp > latest) {
      latest = timestamp
    }
  }
  return latest
}

function getHostLatestTimestamp(host: HostGroup): number {
  let latest = 0
  for (const session of host.sessions) {
    const timestamp = getSessionLatestTimestamp(session)
    if (timestamp > latest) {
      latest = timestamp
    }
  }
  return latest
}

function loadInitialExpansionState(prefix?: string): ExpansionState {
  if (!prefix) return {}

  try {
    const stored = localStorage.getItem(`${prefix}-expansion`)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch {
    void 0
  }

  return {}
}

function saveExpansionState(prefix: string, state: ExpansionState): void {
  try {
    localStorage.setItem(`${prefix}-expansion`, JSON.stringify(state))
  } catch {
    void 0
  }
}

const emptyMessageSx: SxProps<Theme> = {
  fontStyle: 'italic',
  p: 2,
}

const treeContainerSx: SxProps<Theme> = {
  fontFamily: 'inherit',
}
